import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fxRevaluations, type FxPreviewItem } from '@/api/fxRevaluations';
import { orgs } from '@/api/catalogs';
import { accounts as accountsApi } from '@/api/documents';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { MONTHS_UK } from '@/shared/components/PeriodFilter';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import DatePopover from '@/components/DatePopover';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  revaluation_date: string;
  period_from: string;   // 'YYYY-MM'
  period_to: string;     // 'YYYY-MM'
  comment: string;
}

const today = new Date().toISOString().slice(0, 10);

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  revaluation_date: today,
  period_from: today.slice(0, 7),
  period_to: today.slice(0, 7),
  comment: '',
};

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtCur = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const KIND_LABEL: Record<string, string> = {
  INCOME: 'дохід',
  EXPENSE: 'витрата',
  NONE: '—',
};


// Період 'YYYY-MM' → дати для payload: period_start=1-ше число, period_end /
// revaluation_date = останній день місяця «по».
function periodPayload(f: FormState) {
  const [ys = 0, ms = 0] = f.period_from.split('-').map(Number);
  const [ye = 0, me = 0] = f.period_to.split('-').map(Number);
  const lastDay = new Date(ye, me, 0).getDate();
  const end = `${f.period_to}-${String(lastDay).padStart(2, '0')}`;
  return {
    period_start: `${f.period_from}-01`,
    period_end: end,
    revaluation_date: end,
    _valid: Boolean(f.period_from && f.period_to && (ys * 100 + ms) <= (ye * 100 + me)),
  };
}

export default function FxRevaluationEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по accounting.manual.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('accounting.manual.manage');
  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accountsApi.list({ chart_code: 'HOZRASCHETNY', limit: 1000 }),
  });
  const docQuery = useQuery({
    queryKey: ['fx-revaluation', id],
    queryFn: () => fxRevaluations.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const numberPreview = useNumberPreview('doc.fx_revaluations', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  // Превʼю «сформувати» на новому документі (рядки без збереження).
  const [previewItems, setPreviewItems] = useState<FxPreviewItem[]>([]);
  // ч.800: попередження recalc/preview — проводки на валютних рахунках без валютної суми.
  const [fxWarnings, setFxWarnings] = useState<string[]>([]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        revaluation_date: d.revaluation_date.slice(0, 10),
        period_from: (d.period_start ?? d.revaluation_date).slice(0, 7),
        period_to: (d.period_end ?? d.revaluation_date).slice(0, 7),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const accountByCode = useMemo(() => {
    const m = new Map<string, string>();
    accountsQ.data?.forEach((a) => m.set(a.id, a.code));
    return m;
  }, [accountsQ.data]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  // Новий документ: рядки з превʼю (не збережені); збережений — з документа.
  const items = isNew
    ? previewItems.map((p) => ({
        id: `p-${p.line_number}`,
        line_number: p.line_number,
        account_id: p.account_id,
        currency_id: p.currency_id,
        subconto_1_id: null,
        subconto_1_type: null,
        currency_balance: p.currency_balance,
        old_uah_balance: p.old_uah_balance,
        new_rate: p.new_rate,
        new_uah_balance: p.new_uah_balance,
        fx_diff: p.fx_diff,
        entry_kind: p.entry_kind,
        revaluation_date: p.revaluation_date,
      }))
    : (docQuery.data?.items ?? []);

  const create = useMutation({
    mutationFn: async () => {
      const pp = periodPayload(form);
      const doc = await fxRevaluations.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        revaluation_date: pp.revaluation_date, period_start: pp.period_start, period_end: pp.period_end,
        comment: form.comment || null,
      });
      // Рядки довідки зберігаємо разом з документом (те, що показувало превʼю).
      await fxRevaluations.recalc(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
      navigate(`/fx-revaluations/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => fxRevaluations.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      ...(() => { const p = periodPayload(form); return { revaluation_date: p.revaluation_date, period_start: p.period_start, period_end: p.period_end }; })(),
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fx-revaluation', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // «сформувати» (як у 1С): новий документ — зберегти + розрахувати курсові
  // різниці одним кліком; збережений — зберегти правки шапки + перерахувати.
  // «сформувати»: НОВИЙ документ → ПРЕВʼЮ без збереження (документ у списку
  // зʼявиться лише після «зберегти» — зауваження власника 2026-08-23);
  // збережений → зберегти шапку + перерахувати рядки.
  const recalc = useMutation({
    mutationFn: async () => {
      const pp = periodPayload(form);
      if (isNew) {
        const res = await fxRevaluations.preview({
          organization_id: form.organization_id,
          period_start: pp.period_start,
          period_end: pp.period_end,
        });
        setPreviewItems(res.items);
        setFxWarnings(res.warnings ?? []);
        return null;
      }
      await fxRevaluations.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        revaluation_date: pp.revaluation_date, period_start: pp.period_start, period_end: pp.period_end,
        comment: form.comment || null,
      });
      const r = await fxRevaluations.recalc(id!);
      setFxWarnings(r.warnings ?? []);
      return id!;
    },
    onSuccess: (docId) => {
      if (docId) {
        qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
        qc.invalidateQueries({ queryKey: ['fx-revaluation', docId] });
      }
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await fxRevaluations.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        ...(() => { const p = periodPayload(form); return { revaluation_date: p.revaluation_date, period_start: p.period_start, period_end: p.period_end }; })(),
        comment: form.comment || null,
      });
      await fxRevaluations.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluation', id] });
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => fxRevaluations.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluation', id] });
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => fxRevaluations.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['fx-revaluations'] });
      navigate('/fx-revaluations');
    },
  });

  // UX β: warning якщо новий документ а user не має accounting.manual.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати переоцінку валюти у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/fx-revaluations')}>Скасувати</button>
        </div>
      </div>
    );
  }

  // ч.917 (новий дизайн, Закон #10): «ще дій» у документа не було.
  const railActions: { label: string; title?: string; disabled?: boolean; onClick: () => void }[] = [];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 2,
      filled: (form.organization_id ? 1 : 0) + (form.document_date ? 1 : 0) },
    { id: 'period', title: 'Період переоцінки', required: 2,
      filled: (form.period_from ? 1 : 0) + (form.period_to ? 1 : 0) },
    { id: 'items', title: 'Курсові різниці', required: 1,
      filled: items.length > 0 ? 1 : 0 },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];

  return (
    <div className="card">
      {/* ч.917: шапка одним рядком — назва ліворуч, тулбар праворуч. */}
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? 'Довідка-розрахунок переоцінки валюти'
            : `Довідка-розрахунок переоцінки валюти № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          <DocumentToolbar
            railMode
            extraActions={railActions}
            onBackToList={() => navigate('/fx-revaluations')}
            onSave={() => isNew ? create.mutate() : update.mutate()}
            saveDisabled={create.isPending || update.isPending}
            savePending={create.isPending || update.isPending}
            isPosted={isPosted}
            isNew={isNew}
            postable={isPosted}
            onPost={() => updateAndPost.mutate()}
            postPending={updateAndPost.isPending || update.isPending}
            onUnpost={() => unpost.mutate()}
            unpostPending={unpost.isPending}
            onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }) }}
            deletePending={remove.isPending}
            onPrint={() => navigate(`/fx-revaluations/${id}/print`)}
          />
        </div>
      </div>

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew}
          doc={{ type: 'doc.fx_revaluations', id: isNew ? '' : id! }} />
      }>
      {/* ч.917 — блоками (коментар — в останньому блоці). */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Організація *</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>№ документа</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={!isNew}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Дата документа *</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={!isNew}
          />
        </div>
      </div>
      </DocBlock>

      {/* Період довідки-розрахунку (рішення власника 2026-08-23). Розрахунок =
          помісячна розбивка на кінець кожного місяця періоду; проводки робить
          «Закриття місяця». ч.917: «сформувати» — дія таблиці, у шапці блоку 3. */}
      <DocBlock index={2} title="Період переоцінки">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap' }}>
        <div className="field" style={{ margin: 0 }}>
          <label>Період: з *</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={Number(form.period_from.split('-')[1] || 0)} disabled={!isNew}
              onChange={(e) => setForm({ ...form, period_from: `${form.period_from.split('-')[0]}-${String(e.target.value).padStart(2, '0')}` })}
              style={{ width: 130 }}>
              <option value={0} disabled>місяць</option>
              {MONTHS_UK.slice(1).map((m, i2) => <option key={i2 + 1} value={i2 + 1}>{m}</option>)}
            </select>
            <select value={form.period_from.split('-')[0]} disabled={!isNew}
              onChange={(e) => setForm({ ...form, period_from: `${e.target.value}-${form.period_from.split('-')[1]}` })}
              style={{ width: 90 }}>
              {Array.from({ length: 10 }, (_, i2) => new Date().getFullYear() - 8 + i2).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label>по *</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={Number(form.period_to.split('-')[1] || 0)} disabled={!isNew}
              onChange={(e) => setForm({ ...form, period_to: `${form.period_to.split('-')[0]}-${String(e.target.value).padStart(2, '0')}` })}
              style={{ width: 130 }}>
              <option value={0} disabled>місяць</option>
              {MONTHS_UK.slice(1).map((m, i2) => <option key={i2 + 1} value={i2 + 1}>{m}</option>)}
            </select>
            <select value={form.period_to.split('-')[0]} disabled={!isNew}
              onChange={(e) => setForm({ ...form, period_to: `${e.target.value}-${form.period_to.split('-')[1]}` })}
              style={{ width: 90 }}>
              {Array.from({ length: 10 }, (_, i2) => new Date().getFullYear() - 8 + i2).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Курсові різниці"
        actions={!isPosted ? (
          <button
            type="button"
            className="btn-doc"
            onClick={() => recalc.mutate()}
            disabled={recalc.isPending || !form.organization_id || !periodPayload(form)._valid}
            title="Розрахувати курсові різниці на кінець кожного місяця обраного періоду (курси НБУ підтягнуться автоматично)"
          >
            {recalc.isPending ? 'рахуємо…' : 'сформувати'}
          </button>
        ) : undefined}>
      {fxWarnings.length > 0 && (
        <div style={{ background: 'var(--badge-amber-bg)', color: 'var(--badge-amber-fg)', border: '1px solid var(--badge-amber-bd)',
          padding: '0.5rem 0.75rem', borderRadius: 6, marginBottom: '0.75rem', fontSize: '0.85rem' }}>
          ⚠ Є проводки на валютних рахунках без валютної суми — переоцінка по них неточна
          (для мігрованих з 1С — повторна вигрузка з валютою):
          <ul style={{ margin: '0.3rem 0 0', paddingLeft: '1.2rem' }}>{fxWarnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
        </div>
      )}
      {items.length === 0 && !isPosted && (recalc.isSuccess || (!isNew && !docQuery.isLoading)) && (
        <div style={{ padding: '0.75rem 1rem', background: 'var(--badge-amber-bg)',
          border: '1px solid var(--badge-amber-bd)', borderRadius: 6, color: 'var(--badge-amber-fg)', fontSize: '0.9rem' }}>
          <strong>Валютних залишків не знайдено.</strong> За обраний період на валютних
          рахунках (312, 314, 362, 632…) немає залишків у валюті за даними журналу
          проводок. Для баз, мігрованих з 1С: якщо валютні операції точно були —
          ймовірно, проводки з 1С приїхали без сум у валюті (обробка вивантажує лише
          гривневі суми); тоді переоцінка почне працювати з операцій, створених уже
          в нашій системі, або після довантаження валютних даних з 1С.
        </div>
      )}

      {items.length > 0 && (
          <TchTable>
            <thead>
              <tr>
                <th style={{ width: 40 }}>#</th>
                <th style={{ width: 80 }}>Рах.</th>
                <th style={{ width: 130, textAlign: 'right' }}>Залишок (вал.)</th>
                <th style={{ width: 100, textAlign: 'right' }}>Курс</th>
                <th style={{ width: 130, textAlign: 'right' }}>Стара UAH</th>
                <th style={{ width: 130, textAlign: 'right' }}>Нова UAH</th>
                <th style={{ width: 130, textAlign: 'right' }}>Різниця</th>
                <th style={{ width: 100 }}>Тип</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                // Помісячна розбивка: підзаголовок місяця перед першим рядком місяця.
                const md = it.revaluation_date ?? form.revaluation_date;
                const prev = idx > 0 ? (items[idx - 1]?.revaluation_date ?? form.revaluation_date) : null;
                const monthHeader = md !== prev ? (
                  <tr key={`m-${md}`} style={{ background: 'var(--bg-soft)', fontWeight: 700 }}>
                    <td colSpan={8}>
                      {MONTHS_UK[new Date(md).getMonth() + 1]} {new Date(md).getFullYear()} — на {new Date(md).toLocaleDateString('uk-UA')}
                    </td>
                  </tr>
                ) : null;
                return (<Fragment key={it.id}>
                  {monthHeader}
                  {(() => {
                const diff = Number(it.fx_diff);
                const bg = it.entry_kind === 'INCOME' ? '#dcfce7'
                  : it.entry_kind === 'EXPENSE' ? '#fee2e2' : undefined;
                return (
                  <tr>
                    <td>{it.line_number}</td>
                    <td style={{ fontWeight: 600 }}>
                      {accountByCode.get(it.account_id) ?? '?'}
                    </td>
                    <td style={{ textAlign: 'right' }}>{fmtCur(Number(it.currency_balance))}</td>
                    <td style={{ textAlign: 'right' }}>{fmtCur(Number(it.new_rate))}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{fmt(Number(it.old_uah_balance))}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(Number(it.new_uah_balance))}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, background: bg }}>
                      {diff > 0 ? '+' : ''}{fmt(diff)}
                    </td>
                    <td>{KIND_LABEL[it.entry_kind] ?? it.entry_kind}</td>
                  </tr>
                );
                  })()}
                </Fragment>);
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} style={{ textAlign: 'right' }}>Усього:</td>
                <td style={{ textAlign: 'right' }}>
                  <span style={{ color: 'var(--badge-green-fg)' }}>+{fmt(Number(docQuery.data?.total_income ?? 0))}</span>
                  {' / '}
                  <span style={{ color: 'var(--badge-red-fg)' }}>-{fmt(Number(docQuery.data?.total_expense ?? 0))}</span>
                </td>
                <td></td>
              </tr>
            </tfoot>
          </TchTable>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            disabled={!isNew}
          />
        </div>
        <div>
          {/* Вкладення (2026-08-13, рішення власника: на всіх документах). */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType="doc.fx_revaluations" documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
